import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  type AttachmentConstructorData,
  Attachment,
} from '@webapp/core/entities/internal/Attachment'
import {
  CustomFieldValue,
  type CustomFieldValueConstructorData,
} from '@webapp/core/entities/internal/CustomField'
import { Account, type AccountConstructor } from '@webapp/platform/finance'
import { type UserConstructor } from '@webapp/platform/iam'
import { orderItemTotalCostInBaseCurrency } from '../../utils'
import { type OrderItemDurationEnum } from '../enums/OrderItemDurationEnum'
import { type OrderItemFrequencyEnum } from '../enums/OrderItemFequencyEnum'
import {
  OrderItemReceiveMethodTypes,
  type OrderItemReceiveMethodEnum,
} from '../enums/OrderItemReceiveMethodEnum'
import { type OrderItemStatusEnum } from '../enums/OrderItemStatusEnum'
import {
  OrderItemTypes,
  type OrderItemTypeEnum,
} from '../enums/OrderItemTypeEnum'
import { type PunchOutStatusEnum } from '../enums/PunchOutStatusEnum'
import {
  OrderItemVendor,
  type OrderItemVendorConstructor,
} from '../OrderItemVendor'

export interface OrderItemConstructorData {
  id: number
  name: string
  type: OrderItemTypeEnum
  status: OrderItemStatusEnum
  dateSubmitted: ISODateString
  dateRequired: ISODateString
  dateModified: ISODateString
  account: AccountConstructor
  customFields: CustomFieldValueConstructorData[]
  attachments: AttachmentConstructorData[]
  sku: string
  comment: Nullable<string>
  vendorComment: Nullable<string>
  currency: CurrencyConstructor
  price: string
  quantity: number
  unit: string
  shippingAmount: Nullable<string>
  taxAmount: Nullable<string>
  totalCost: string
  approvedTotalCost: Nullable<string>
  preferredVendor: OrderItemVendorConstructor
  vendorName: string
  receivingMethod: OrderItemReceiveMethodEnum // Only relevant for blanket items (see blanketItemDetails)
  blanketItemDetails: Nullable<{
    amount: Nullable<number>
    quantity: Nullable<number>
    price: Nullable<string>
  }>
  recurringItemDetails: Nullable<{
    startDate: ISODateString
    frequency: OrderItemFrequencyEnum
    duration: OrderItemDurationEnum
    durationQuantity: number
    recurringPrice: string
    periodsCount: number
  }>
  catalogItem: Nullable<{
    id: number
    name: Nullable<string>
    locked: boolean
  }>
  punchoutSupplier: Nullable<{
    id: number
    icon: string
    name: string
    canEditQuantity: boolean
  }>
  punchoutConfirmationStatus: Nullable<{
    status: PunchOutStatusEnum
    description: string
  }>
  order: {
    uuid: uuid
    id: number
    orderNumber: number
  }
  purchaseOrder: Nullable<{
    id: number
    purchaseOrderNumber: Nullable<string>
    uuid: string
    comment: string
    purchaser: Nullable<UserConstructor>
  }>
  receivedQuantity: number
  dateFullyReceived: Nullable<ISODateString>
  lineNumber: number
}

export class OrderItem {
  id: number
  name: string
  type: OrderItemTypeEnum
  status: OrderItemStatusEnum
  dateSubmitted: Date
  dateRequired: Date
  dateModified: Date
  account: Account
  customFields: CustomFieldValue[]
  attachments: Attachment[]
  sku: string
  comment: Nullable<string>
  vendorComment: Nullable<string>
  currency: Currency
  private readonly _price: string
  private readonly _quantity: number
  unit: string
  shippingAmount: Nullable<string>
  taxAmount: Nullable<string>
  totalCost: string
  approvedTotalCost: Nullable<string>
  vendor: OrderItemVendor
  lineNumber: number
  receivedQuantity: number
  dateFullyReceived: Nullable<YYYYMMDDDateString>
  blanketItemDetails: Nullable<{
    type: OrderItemReceiveMethodEnum
    amount: Nullable<number> // If receiving by currency amount, this will specify the amount; Otherwise null
    quantity: Nullable<number> // If receiving by quantity, the quantity will specify the number of items; Othwerwise null
    price: Nullable<string> // If receiving by quantity, this will specify the price per item; Otherwise null
  }>
  recurringItemDetails: Nullable<{
    startDate: Date
    frequency: OrderItemFrequencyEnum
    duration: OrderItemDurationEnum
    durationQuantity: number
    recurringPrice: string
    periodsCount: number
  }>
  catalogItem: Nullable<{
    id: number
    name: Nullable<string>
    locked: boolean
  }>
  punchoutDetails: Nullable<{
    supplier: {
      id: number
      icon: string
      name: string
      canEditQuantity: boolean
    }
    confirmationStatus: Nullable<{
      status: PunchOutStatusEnum
      description: string
    }>
  }>
  order: {
    uuid: uuid
    id: number
    orderNumber: number
  }
  purchaseOrder: Nullable<{
    id: number
    purchaseOrderNumber: Nullable<string>
    uuid: string
    comment: string
    purchaser: Nullable<UserConstructor>
  }>

  constructor(data: OrderItemConstructorData) {
    this.id = data.id
    this.name = data.name
    this.type = data.type
    this.status = data.status
    this.dateSubmitted = new Date(data.dateSubmitted)
    this.dateRequired = new Date(data.dateRequired)
    this.dateModified = new Date(data.dateModified)
    this.account = new Account(data.account)
    this.sku = data.sku
    this.comment = data.comment
    this.vendorComment = data.vendorComment
    this.currency = new Currency(data.currency)
    this._price = data.price
    this._quantity = data.quantity
    this.unit = data.unit
    this.shippingAmount = data.shippingAmount
    this.taxAmount = data.taxAmount
    this.totalCost = data.totalCost
    this.approvedTotalCost = data.approvedTotalCost
    this.order = data.order
    this.purchaseOrder = data.purchaseOrder

    this.catalogItem = data.catalogItem
    this.lineNumber = data.lineNumber

    this.receivedQuantity = data.receivedQuantity
    this.dateFullyReceived = data.dateFullyReceived
      ? data.dateFullyReceived
      : null

    this.attachments = data.attachments.map(
      (attachment) => new Attachment(attachment)
    )

    // Make use of vendorName over the name value of the preferredVendor object. The name value of the preferredVendor
    // is the same as the vendorName value, except for when the selected preferred vendor is "Other". In this case, the
    // name value of the preferredVendor object is "Other" and the vendorName value is the actual name of the vendor.
    this.vendor = new OrderItemVendor({
      ...data.preferredVendor,
      name: data.vendorName,
    })

    // id and value may be null if the custom field was created after the order item was created. We still need
    // to keep entries of such kind within this list so that we can render them in the UI even though there is
    // no value associated with them.
    this.customFields = data.customFields.map(
      (value) => new CustomFieldValue(value)
    )

    // punchoutDetails should only be set if we have a valid punchout supplier
    if (data.punchoutSupplier) {
      this.punchoutDetails = {
        supplier: data.punchoutSupplier,
        confirmationStatus: data.punchoutConfirmationStatus,
      }
    } else {
      this.punchoutDetails = null
    }

    // blanketItemDetails should only be set if we have a valid blanket item
    if (
      data.blanketItemDetails &&
      data.receivingMethod === OrderItemReceiveMethodTypes.CURRENCY_AMOUNT
    ) {
      this.blanketItemDetails = {
        type: data.receivingMethod,
        amount: data.blanketItemDetails.amount,
        quantity: null,
        price: null,
      }
    } else if (
      data.blanketItemDetails &&
      data.receivingMethod === OrderItemReceiveMethodTypes.QUANTITY
    ) {
      this.blanketItemDetails = {
        type: data.receivingMethod,
        amount: null,
        quantity: data.blanketItemDetails.quantity,
        price: data.blanketItemDetails.price,
      }
    } else {
      this.blanketItemDetails = null
    }

    // recurringItemDetails should only be set if we have a valid recurring item
    if (data.recurringItemDetails) {
      this.recurringItemDetails = {
        startDate: new Date(data.recurringItemDetails.startDate),
        frequency: data.recurringItemDetails.frequency,
        duration: data.recurringItemDetails.duration,
        durationQuantity: data.recurringItemDetails.durationQuantity,
        recurringPrice: data.recurringItemDetails.recurringPrice,
        periodsCount: data.recurringItemDetails.periodsCount,
      }
    } else {
      this.recurringItemDetails = null
    }
  }

  get isBlanketItem() {
    return this.type === OrderItemTypes.BLANKET
  }

  get isPunchOutItem() {
    return Boolean(this.punchoutDetails)
  }

  get isRecurringItem() {
    return this.type === OrderItemTypes.RECURRING
  }

  get price() {
    if (this.isBlanketItem && this.blanketItemDetails) {
      if (
        this.blanketItemDetails.type ===
        OrderItemReceiveMethodTypes.CURRENCY_AMOUNT
      ) {
        return this.blanketItemDetails.amount?.toString() ?? '0'
      } else {
        return this.blanketItemDetails.price ?? '0'
      }
    }

    return this._price
  }

  get quantity() {
    if (this.isBlanketItem && this.blanketItemDetails) {
      if (
        this.blanketItemDetails.type ===
        OrderItemReceiveMethodTypes.CURRENCY_AMOUNT
      ) {
        return 1
      } else {
        return this.blanketItemDetails.quantity ?? 0
      }
    }

    return this._quantity
  }

  get isCatalogItem() {
    return this.catalogItem !== null
  }

  // TODO: remove includeTaxAndShipping along with CHQ_34_ADD_AMZNPO_TAX_SHIP and convert this to a computed property
  totalCostInBaseCurrency(includeTaxAndShipping: boolean) {
    return orderItemTotalCostInBaseCurrency({
      price: this.price,
      currencyRate: this.currency.rate,
      quantity: this.quantity,
      shippingAmount: this.shippingAmount,
      taxAmount: this.taxAmount,
      includeTaxAndShipping,
    })
  }
}
